/** Data hooks of the saved views (2.K deliverable 8): the list and the five writes. */
import {
  ApiError,
  type SavedViewCreateV1,
  type SavedViewUpdateV1,
  type SavedViewV1,
  type SidebarItemV1,
  sidebarRemove,
  viewsCreate,
  viewsList,
  viewsPin,
  viewsRemove,
  viewsUpdate,
} from '@codex/contracts';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../../auth/client.js';
import { sidebarKey } from '../../shell/queries.js';

export const viewsKey = ['views'] as const;
export const viewsListKey = (typeKey?: string) => ['views', typeKey ?? 'all'] as const;

/**
 * Refreshes what a pin change touches: the sidebar's list and every saved-view list. The
 * sidebar's own Unpin of a view row goes through it, so a view page open beside it drops its
 * pinned state (the shell's client never refetches on focus).
 */
export async function invalidatePinQueries(client: QueryClient): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: sidebarKey }),
    client.invalidateQueries({ queryKey: viewsKey }),
  ]);
}

/** `views.list`, every type's or one type's. `enabled: false` sends nothing. */
export function useSavedViews(typeKey?: string, enabled = true) {
  return useQuery({
    queryKey: viewsListKey(typeKey),
    enabled,
    queryFn: async () =>
      (await api(viewsList, { query: typeKey === undefined ? {} : { typeKey } })).items,
    retry: false,
  });
}

/** One view, selected from the unfiltered list (`views.list` has no single-view route). */
export function useSavedView(id: string | null) {
  const list = useSavedViews(undefined, id !== null);
  const view = useMemo(
    () => (id === null ? null : (list.data?.find((v) => v.id === id) ?? null)),
    [list.data, id],
  );
  return { list, view };
}

/** The `details.issue` of a 422, as `{code, column}`; null for any other failure. */
export function refusalIssue(
  error: unknown,
): { code: string | null; column: string | null } | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const issue = (error.body.details as { issue?: { code?: unknown; column?: unknown } } | undefined)
    ?.issue;
  if (typeof issue !== 'object' || issue === null) return { code: null, column: null };
  return {
    code: typeof issue.code === 'string' ? issue.code : null,
    column: typeof issue.column === 'string' ? issue.column : null,
  };
}

export const isConflict = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 409;

export interface ViewMutations {
  readonly create: (body: SavedViewCreateV1) => Promise<SavedViewV1>;
  readonly update: (id: string, body: SavedViewUpdateV1) => Promise<SavedViewV1>;
  /** `onRemoved` runs once the server has deleted the view, before the lists refresh. */
  readonly remove: (id: string, onRemoved?: () => void) => Promise<void>;
  readonly pin: (id: string) => Promise<SidebarItemV1>;
  readonly unpin: (itemId: string) => Promise<void>;
}

/** The writes; each refreshes `['views']` and the sidebar's list when it succeeds. */
export function useViewMutations(): ViewMutations {
  const client = useQueryClient();
  return useMemo(() => {
    const refresh = () => invalidatePinQueries(client);
    return {
      create: async (body) => {
        const made = await api(viewsCreate, { body });
        await refresh();
        return made;
      },
      update: async (id, body) => {
        const made = await api(viewsUpdate, { params: { id }, body });
        await refresh();
        return made;
      },
      remove: async (id, onRemoved) => {
        await api(viewsRemove, { params: { id } });
        onRemoved?.();
        await refresh();
      },
      pin: async (id) => {
        const item = await api(viewsPin, { params: { id }, body: {} });
        await refresh();
        return item;
      },
      unpin: async (itemId) => {
        await api(sidebarRemove, { params: { id: itemId } });
        await refresh();
      },
    };
  }, [client]);
}
